// 启动模块状态管理

import { createWithEqualityFn } from "zustand/traditional";
import { subscribeWithSelector } from "zustand/middleware";
import { StartupState, StartupActions } from "../types";
import { startupService } from "../services";
import { BaseCache } from "../../base/stores";

// 创建启动模块专用的缓存实例
const startupCache = new BaseCache({
  defaultTTL: 5 * 60 * 1000, // 5分钟
  maxSize: 50,
  enableLogging: true,
});

// 初始状态
const initialState: StartupState = {
  startupData: null,
  startupLoading: "idle",
  startupError: null,
  cache: new Map(),
};

// 创建启动store
export const useStartupStore = createWithEqualityFn<
  StartupState & StartupActions
>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,

    // 启动数据相关
    setStartupData: data => set({ startupData: data }),
    setStartupLoading: loading => set({ startupLoading: loading }),
    setStartupError: error => set({ startupError: error }),
    loadStartupData: async lazy => {
      const { setStartupLoading, setStartupError, setStartupData } = get();

      try {
        setStartupLoading("loading");
        setStartupError(null);

        const cacheKey = `startup_data_${lazy}`;

        // 使用缓存优先的 API 调用
        const data = await startupCache.cacheFirst(
          cacheKey,
          () => startupService.loadStartupData(lazy),
          5 * 60 * 1000 // 5分钟缓存
        );

        setStartupData(data);
        setStartupLoading("success");
        return data;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "load startup data failed";
        setStartupError(errorMessage);
        setStartupLoading("error");
        throw error;
      }
    },

    // 缓存相关 - 委托给 BaseCache 实例
    setCache: (key, data, ttl) => {
      startupCache.setCache(key, data, ttl);
      set({ cache: new Map(startupCache["cache"]) });
    },

    getCache: key => {
      return startupCache.getCache(key);
    },

    clearCache: pattern => {
      startupCache.clearCache(pattern);
      set({ cache: new Map(startupCache["cache"]) });
    },

    hasCache: key => {
      return startupCache.hasCache(key);
    },

    getCacheKeys: () => {
      return startupCache.getCacheKeys();
    },

    getCacheSize: () => {
      return startupCache.getCacheSize();
    },

    cacheFirst: async (key, apiCall, ttl) => {
      return startupCache.cacheFirst(key, apiCall, ttl);
    },
  }))
);

// 启动数据 hook
export function useStartupData() {
  return useStartupStore(state => ({
    data: state.startupData,
    loading: state.startupLoading,
    error: state.startupError,
  }));
}

// 扁平化的启动数据 hook - 直接导出 StartupData 的所有属性
export function useStartupDataFlattened() {
  return useStartupStore(state => {
    const { startupData, startupLoading, startupError } = state;

    // 如果 startupData 为 null，返回所有属性为 undefined
    if (!startupData) {
      return {
        // 状态信息
        loading: startupLoading,
        error: startupError,
        data: null,

        // StartupData 的所有属性，当 data 为 null 时都设为 undefined
        countryId: undefined,
        countries: undefined,
        currencyId: undefined,
        currencies: undefined,
        languages: undefined,
        buildConfig: undefined,
        enabledSmsConfigs: undefined,
        smsConfig: {
          enableForgetPassword: false,
          enableRegister: false,
          enableWithdraw: false,
          enableLogin: false,
          defaultLoginType: "EMAIL",
        },
        gpcSymbol: undefined,
        themeId: undefined,
        horizontalCom: undefined,
        gpcIcon: undefined,
        enableFinanceWallet: undefined,
        enableWebPush: undefined,
        disableGPC: undefined,
        language: undefined,
        disableVipPlus: undefined,
        langItemsCacheKey: undefined,
        designCacheKey: undefined,
        minBetAmount: undefined,
        defaultBetAmount: undefined,
        brand: undefined,
        vpCode: undefined,
        enableNewStyle: undefined,
        apkVersion: undefined,
        sk: undefined,
        apkLink: undefined,
        apkPackageName: undefined,
        disableAppDownload: undefined,
        disableRechargeAppDownload: undefined,
        enableSalary: undefined,
        enableRegisterPromoCode: undefined,
        autoBk: undefined,
        appThemeVersion: undefined,
        thirdSetting: undefined,
        firebaseConfig: undefined,
        loginCheck2Auth: undefined,
        enableMultiCurrency: undefined,
        enableInviteWheel: undefined,
        enableRegisterEmailCode: undefined,
        agentStrategyType: undefined,
        offlineTokenWalletItems: undefined,
        subCurrencyConfList: undefined,
        iosMobileFileLink: undefined,
        activityRules: [],
        appType: undefined,
      };
    }

    // 当 startupData 存在时，展开所有属性
    return {
      // 状态信息
      loading: startupLoading,
      error: startupError,
      data: startupData,

      // StartupData 的所有属性
      ...startupData,
    };
  });
}

// 启动服务 hook
export function useStartupService() {
  return useStartupStore(state => ({
    loadStartupData: state.loadStartupData,
    setStartupData: state.setStartupData,
    setStartupLoading: state.setStartupLoading,
    setStartupError: state.setStartupError,
  }));
}

// 启动缓存 hook
export function useStartupCache() {
  return useStartupStore(state => ({
    setCache: state.setCache,
    getCache: state.getCache,
    clearCache: state.clearCache,
    hasCache: state.hasCache,
    getCacheKeys: state.getCacheKeys,
    getCacheSize: state.getCacheSize,
    cacheFirst: state.cacheFirst,
  }));
}
